import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DatePicker, Form, Modal, Skeleton } from 'antd';
import cn from 'classnames';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useTimersQuery, useUpdateTimerMutation } from 'generated/graphql';
import _ from 'lodash';
import { observer } from 'mobx-react-lite';

import { FullSecondsFormat } from '../../shared/TimeFormat';
import { Tag } from '../Board/Tag';

import cls from './Timers.module.scss';

dayjs.extend(duration);

interface IReports {
    taskId: string;
}

interface ITimer {
    id: string;
    startAt: string;
    finishedAt: string;
}

export const Timers = observer(({ taskId }: IReports) => {
    // const store = useInstance(Store);
    const [showAll, setShowAll] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedTimer, setSelectedTimer] = useState<ITimer | null>(null);
    const [form] = Form.useForm();

    const {t} = useTranslation()

    const timers = useTimersQuery({
        variables: {
            filters: {
                task: { id: { eq: taskId } },
            },
            pagination: {
                limit: 1000,
            },
        },
        // pollInterval: 1000,
    });

    const [updateTimer] = useUpdateTimerMutation({
        onCompleted: () => {
            setEditModalOpen(false);
            timers.refetch();
        },
    });

    const totalSeconds: number = useMemo(
        () =>
            _.reduce(
                timers.data?.timers?.data,
                function (sum, item) {
                    if (item.attributes?.finishedAt !== null) {
                        return (
                            sum + dayjs(item.attributes?.finishedAt).diff(dayjs(item.attributes?.startAt), 'seconds')
                        );
                    } else {
                        return sum;
                    }
                },
                0,
            ),
        [timers.data?.timers?.data],
    );

    function shortRange (startAt:string, finishedAt:string|undefined) {

        // start
        const startDate = dayjs(startAt)
        let startDateFormat = ''
        // не сегодня
        if (dayjs().diff(startDate, 'days') !== 0) {
            // этот год
            if (dayjs().diff(startDate, 'year') === 0) {
                startDateFormat = startDate.format('DD MMM')
            } else {
                startDateFormat = startDate.format('DD.MM.YYYY')
            }
        }
        const startTimeFormat = startDate.format('HH:mm')

        //finish
        let finishDateFormat = ''
        let finishTimeFormat = t('Now')
        // если указана дата
        if (finishedAt) {
            const finishDate = dayjs(finishedAt)
            // если начало и конец не совпали
            if (finishDate.diff(startDate, 'days') !== 0) {
                // этот год
                if (dayjs().diff(finishDate, 'year') === 0) {
                    finishDateFormat = startDate.format('DD MMM')
                } else {
                    finishDateFormat = startDate.format('DD.MM.YYYY')
                }
            }
            finishTimeFormat = finishDate.format('HH:mm')
        }




        return `${startDateFormat} ${startTimeFormat} — ${finishDateFormat} ${finishTimeFormat}`
    }

    const handleEditTimer = (timer: any) => {
        setSelectedTimer({
            id: timer.id,
            startAt: timer.attributes?.startAt,
            finishedAt: timer.attributes?.finishedAt,
        });
        form.setFieldsValue({
            startAt: dayjs(timer.attributes?.startAt),
            finishedAt: timer.attributes?.finishedAt ? dayjs(timer.attributes?.finishedAt) : null,
        });
        setEditModalOpen(true);
    };

    const handleCloseModal = () => {
        setEditModalOpen(false);
        form.resetFields();
        setSelectedTimer(null);
    };

    const handleSaveTimer = async (values: { startAt: dayjs.Dayjs; finishedAt: dayjs.Dayjs | null }) => {
        if (!selectedTimer) return;

        await updateTimer({
            variables: {
                id: selectedTimer.id,
                data: {
                    startAt: values.startAt.format(),
                    finishedAt: values.finishedAt ? values.finishedAt.format() : undefined,
                },
            },
        });
    };

    return (
        <div className={cls.reports}>
            {timers.loading && <Skeleton.Input block active />}

            <div className={cls.grid}>
                {_.map(timers.data?.timers?.data, (timer, index) => {
                    const length: number = timers.data?.timers?.data.length!;

                    if (!showAll && length > 4) {
                        if (index === 3) {
                            return (
                                <React.Fragment key={timer.id}>
                                    <div className={cls.user}></div>
                                    <div className={cls.time}></div>
                                    <div 
                                        className={cn(cls.comment, cls.clickable)} 
                                        onClick={() => setShowAll(true)}
                                    >
                                        ...
                                    </div>
                                </React.Fragment>
                            )
                        }
                        if (index >= 2 && index <= length - 2 ) {
                            return null;
                        }

                    }

                    return (
                        <React.Fragment key={timer.id}>
                            <div className={cls.user} >
                                <Tag
                                    data={{
                                        id: timer.attributes?.user?.data?.id!,
                                        name: timer.attributes?.user?.data?.attributes?.username!,
                                        color: timer.attributes?.user?.data?.attributes?.color!,
                                    }}
                                />
                            </div>
                            <div 
                                className={cls.time}
                                onClick={() => handleEditTimer(timer)}
                            >
                                {timer.attributes?.finishedAt
                                    ? FullSecondsFormat(
                                          dayjs(timer.attributes.finishedAt).diff(
                                              dayjs(timer.attributes.startAt),
                                              'seconds',
                                          ),
                                      )
                                    : '🕑'}
                            </div>
                            <div className={cls.comment}>
                                {shortRange(timer.attributes?.startAt, timer.attributes?.finishedAt)}
                            </div>
                        </React.Fragment>
                    );
                })}

                <div className={cls.user}></div>
                <div className={cn([cls.time, 'bold'])}>
                    <b>{FullSecondsFormat(totalSeconds)}</b>
                </div>
                <div className={cls.comment}></div>
            </div>

            <Modal 
                open={editModalOpen} 
                onCancel={handleCloseModal} 
                footer={null} 
                closable={false} 
                width={360}
                title={t('Edit Timer')}
            >
                <Form 
                    layout={'vertical'} 
                    onFinish={handleSaveTimer} 
                    form={form}
                >
                    <Form.Item 
                        name={'startAt'}
                        label={t('Start time')}
                        dependencies={['finishedAt']}
                        rules={[
                            {
                                required: true,
                                message: t('Please enter start time') || '',
                            },
                            {
                                validator: (_, value) => {
                                    const finishedAt = form.getFieldValue('finishedAt');
                                    if (value && finishedAt && dayjs(value).isAfter(dayjs(finishedAt))) {
                                        return Promise.reject(new Error(t('Must be before end') || ''));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <DatePicker 
                            showTime 
                            format="DD.MM.YYYY HH:mm" 
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <Form.Item 
                        name={'finishedAt'}
                        label={t('End time')}
                        dependencies={['startAt']}
                        rules={[
                            {
                                required: true,
                                message: t('Please enter end time') || '',
                            },
                            {
                                validator: (_, value) => {
                                    const startAt = form.getFieldValue('startAt');
                                    if (value && startAt && dayjs(value).isBefore(dayjs(startAt))) {
                                        return Promise.reject(new Error(t('Must be after start') || ''));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <DatePicker 
                            showTime 
                            format="DD.MM.YYYY HH:mm" 
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button htmlType={'submit'} type={'primary'} block>
                            {t('Save')}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
});
